import {
  ChangeDetectionStrategy,
  Component,
  input,
  signal,
  inject,
  computed,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  NgCommonModule,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import { DEFAULT_INVOICE_DUE_DATE_DAYS } from '@principle-theorem/principle-core';
import { IPractice } from '@principle-theorem/principle-core/interfaces';
import { Firestore, WithRef } from '@principle-theorem/shared';

@Component({
  selector: 'pr-invoice-due-date-settings',
  imports: [NgCommonModule, MatInputModule],
  templateUrl: './invoice-due-date-settings.component.html',
  styleUrl: './invoice-due-date-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvoiceDueDateSettingsComponent {
  matSnackbar = inject(MatSnackBar);
  practice = input.required<WithRef<IPractice>>();
  hasMultiplePractices = input<boolean>();
  saving = signal(false);
  practiceName = computed(() => this.practice().name);

  dueDateDays = new TypedFormControl<number>(DEFAULT_INVOICE_DUE_DATE_DAYS, [
    Validators.required,
    Validators.min(0),
  ]);

  constructor() {
    toObservable(this.practice)
      .pipe(takeUntilDestroyed())
      .subscribe((practice) => {
        this.dueDateDays.patchValue(
          practice.settings.accounting?.invoiceDueDateDays ??
            DEFAULT_INVOICE_DUE_DATE_DAYS,
          { emitEvent: false }
        );
      });
  }

  async onDueDateDaysChange(): Promise<void> {
    if (this.dueDateDays.invalid) {
      return;
    }

    const value = this.dueDateDays.value;
    this.saving.set(true);

    try {
      const practice = this.practice();
      const settings = {
        ...practice.settings,
        accounting: {
          ...practice.settings.accounting,
          invoiceDueDateDays: value,
        },
      };
      await Firestore.patchDoc(practice.ref, { settings });
      this.matSnackbar.open('Successfully saved invoice due date setting.');
    } catch {
      this.matSnackbar.open('Failed to save invoice due date setting.');
      this.dueDateDays.patchValue(
        this.practice().settings.accounting?.invoiceDueDateDays ??
          DEFAULT_INVOICE_DUE_DATE_DAYS,
        { emitEvent: false }
      );
    } finally {
      this.saving.set(false);
    }
  }
}
